import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mail,
  Send,
  Search,
  Loader2,
  RefreshCw,
  Trash2,
  Plus,
  Edit,
  X,
  ArrowLeft,
  Reply,
  Forward,
  FileText,
  Eye,
  Star,
  Check,
  Info,
  ChevronDown,
  ChevronRight,
  Inbox,
  SendHorizonal,
  Archive,
  MoreVertical,
  CheckSquare,
  MailCheck,
  MailX,
  Download,
  Paperclip,
  Image,
  File as FileIcon,
  RotateCcw,
  LayoutTemplate,
  AlertTriangle,
  Menu as MenuIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import RichTextEditor, { type RichTextEditorRef } from '@easyflow/ui-frontend/components/ui/rich-text-editor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@easyflow/ui-frontend/components/ui/sheet';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-media-query';
import { useSocketOptional } from '@/contexts/SocketContext';
import { cn, getErrorMessage } from '@/lib/utils';
import EmailComposeModal from '@/components/crm/EmailComposeModal';

interface EmailMessage {
  id: string;
  threadId: string;
  from: string;
  to: string;
  cc: string;
  subject: string;
  snippet: string;
  date: string;
  bodyText: string;
  bodyHtml: string;
  labelIds: string[];
  internalDate: string;
}

interface ImapEmail {
  id: string;
  account_id: string;
  message_id: string;
  uid: number;
  folder: string;
  folder_type?: string;
  from_address: string;
  from_name: string;
  to_address: string;
  cc: string;
  subject: string;
  body_text: string;
  body_html: string;
  date: string;
  is_read: boolean;
  has_attachments: boolean;
  attachments: EmailAttachment[] | string | null;
  account_name: string;
  account_email: string;
  account_type: string;
  thread_key?: string | null;
  in_reply_to?: string | null;
  references_header?: string | null;
}

interface EmailAttachment {
  filename?: string;
  contentType?: string;
  size?: number;
  [key: string]: unknown;
}

interface UnifiedEmail {
  id: string;
  source: 'gmail' | 'imap';
  accountId: string;
  accountName: string;
  accountEmail: string;
  accountType: string;
  google_account_id?: string;
  threadId?: string;
  // Task #896: chiave di raggruppamento del thread (server-side per IMAP,
  // gmail.threadId per Gmail). `threadCount` è popolato solo quando l'email
  // è la "testa" di un thread con > 1 messaggi.
  threadKey?: string | null;
  threadCount?: number;
  from: string;
  fromName: string;
  to: string;
  cc: string;
  subject: string;
  snippet: string;
  date: string;
  bodyText: string;
  bodyHtml: string;
  isUnread: boolean;
  hasAttachments: boolean;
  attachments?: EmailAttachment[] | string | null;
  labelIds?: string[];
}

interface EmailThread {
  threadId: string;
  messages: EmailMessage[];
}

interface EmailTemplate {
  id: string;
  tenant_id: string;
  name: string;
  subject: string;
  body_html: string;
  signature_html: string;
  variables: string[];
  is_default: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface VariableCategory {
  category: { id: string; name: string; display_name: string; icon: string };
  variables: { id: string; variable_key: string; display_name: string; example_value: string }[];
}

interface UnifiedEmailAccount {
  id: string;
  account_type: 'gmail' | 'smtp';
  display_name: string;
  email_address: string;
  is_default: boolean;
  google_account_id?: string;
}

type FolderType = 'inbox' | 'sent' | 'templates' | 'trash' | 'archive';

function formatRelativeDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Adesso';
  if (diffMin < 60) return `${diffMin} min fa`;
  if (diffHours < 24) return `${diffHours} ore fa`;
  if (diffDays < 7) return `${diffDays}g fa`;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

function extractName(from: string): string {
  if (!from) return '';
  const match = from.match(/^(.+?)\s*<.*>$/);
  return match ? match[1]!.trim().replace(/^["']|["']$/g, '')! : from;
}

function extractEmail(from: string): string {
  if (!from) return '';
  const match = from.match(/<(.+?)>/);
  return match ? match[1]! : from;
}

function getInitials(name: string): string {
  if (!name) return '?';
  const clean = name.replace(/^["']|["']$/g, '').trim();
  const parts = clean.split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return clean.substring(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
    'bg-orange-500', 'bg-pink-500', 'bg-violet-500', 'bg-lime-600',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length]!;
}

// Task #896: stessa normalizzazione del backend per il fallback di
// raggruppamento sui messaggi Gmail (che usano il threadId nativo) ed
// eventuali messaggi senza thread_key.
function normalizeSubjectForThreading(subject: string | null | undefined): string {
  if (!subject) return '';
  let s = subject.trim();
  const prefixRe = /^\s*(re|fwd?|fw|i|r|tr|antw|aw|sv|wg|rv|réf|enc)\s*[:\.\-]\s*/i;
  let prev: string;
  do {
    prev = s;
    s = s.replace(prefixRe, '').trim();
  } while (s !== prev && s.length > 0);
  return s.replace(/\s+/g, ' ').toLowerCase();
}

// Task #896: estrae l'indirizzo email "puro" da una stringa `"Mario" <m@x.it>`.
function extractEmailAddrFE(value: string | null | undefined): string {
  if (!value) return '';
  const m = value.match(/<([^>]+)>/);
  const raw = (m ? m[1]! : value).trim().toLowerCase();
  return raw.replace(/^["']|["']$/g, '').trim();
}

// Task #896: firma partecipanti (set ordinato di indirizzi) per discriminare
// thread con stesso subject ma controparti diverse.
function buildParticipantsKeyFE(from: string | null | undefined, to: string | null | undefined, cc: string | null | undefined): string {
  const parts: string[] = [];
  for (const field of [from, to, cc]) {
    if (!field) continue;
    for (const piece of String(field).split(/[,;]/)) {
      const addr = extractEmailAddrFE(piece);
      if (addr && addr.includes('@')) parts.push(addr);
    }
  }
  if (parts.length === 0) return '';
  return Array.from(new Set(parts)).sort().join('|');
}

// Task #896: thread-key di fallback identico al backend (vedi
// buildFallbackThreadKey in custom-email.service.ts) — subject normalizzato
// + firma partecipanti.
function buildFallbackThreadKeyFE(e: UnifiedEmail): string {
  const subj = normalizeSubjectForThreading(e.subject);
  const participants = buildParticipantsKeyFE(e.from, e.to, e.cc);
  if (!subj && !participants) return '';
  return `s:${subj}|p:${participants}`;
}

// Task #896: raggruppa una lista di email in "thread". Per Gmail usiamo il
// threadId nativo, per IMAP la `threadKey` calcolata lato server con
// fallback robusto (subject normalizzato + partecipanti) per i record
// legacy che non hanno ancora `thread_key` valorizzato. Restituisce solo
// la "testa" di ciascun thread (la più recente) con `threadCount` impostato
// quando i messaggi sono > 1.
function groupEmailsByThread(emails: UnifiedEmail[]): UnifiedEmail[] {
  const groups = new Map<string, UnifiedEmail[]>();
  for (const e of emails) {
    let key: string;
    if (e.source === 'gmail' && e.threadId) {
      key = `gmail:${e.accountId || ''}:${e.threadId}`;
    } else if (e.source === 'imap') {
      // Per gli IMAP usa la chiave del server quando disponibile, altrimenti
      // usa il fallback subject+partecipanti — coerente con il backend.
      const k = e.threadKey || buildFallbackThreadKeyFE(e) || `id:${e.id}`;
      key = `imap:${e.accountId || ''}:${k}`;
      // Memorizza la chiave calcolata sull'email così l'apertura del thread
      // (vedi handleEmailClick) può cercare i messaggi correlati anche per
      // i record legacy.
      if (!e.threadKey) e.threadKey = k;
    } else {
      key = `other:${e.id}`;
    }
    const arr = groups.get(key) || [];
    arr.push(e);
    groups.set(key, arr);
  }
  const heads: UnifiedEmail[] = [];
  for (const arr of groups.values()) {
    arr.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const head = { ...arr[0]! };
    if (arr.length > 1) {
      head.threadCount = arr.length;
      // Se almeno un messaggio del thread non è letto, marca il thread come non letto.
      head.isUnread = arr.some(m => m.isUnread);
    }
    heads.push(head);
  }
  heads.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return heads;
}

function sanitizeHtml(html: string): string {
  const cleaned = html.replace(/src\s*=\s*["']?cid:[^"'\s>]*["']?/gi,
    'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"');
  return DOMPurify.sanitize(cleaned, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['style'],
    ADD_ATTR: ['target', 'style'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  });
}

// Task #915: payload dell'evento Socket.IO `email:moved` emesso dal backend
// dopo le rotte archive / unarchive / soft-delete / restore. Permette agli
// altri tab di aggiornarsi in tempo reale senza rifare il sync.
type MovedFolderType = 'inbox' | 'sent' | 'archive' | 'trash' | 'drafts';
interface EmailMovedEvent {
  source: 'imap' | 'gmail';
  email_id: string;
  account_id: string | null;
  from_folder_type: MovedFolderType | null;
  to_folder_type: MovedFolderType;
  was_unread: boolean;
  email?: ImapEmail | null;
}

// Task #915: converte un record `received_emails` (servito dal backend) in
// `UnifiedEmail` riusando la stessa proiezione di `loadInbox` / `loadTrash`
// / `loadArchive`. Centralizzato qui per restare allineato quando il
// payload del socket arriva e dobbiamo mostrare il messaggio nella
// cartella di destinazione senza ricaricare.
function imapRowToUnified(e: ImapEmail): UnifiedEmail {
  return {
    id: `imap_${e.id}`,
    source: 'imap',
    accountId: e.account_id,
    accountName: e.account_name || '',
    accountEmail: e.account_email || '',
    accountType: e.account_type || 'smtp',
    threadKey: e.thread_key || null,
    from: e.from_name ? `${e.from_name} <${e.from_address}>` : e.from_address,
    fromName: e.from_name || e.from_address || '',
    to: e.to_address,
    cc: e.cc || '',
    subject: e.subject,
    snippet: (e.body_text || '').substring(0, 120),
    date: e.date,
    bodyText: e.body_text,
    bodyHtml: e.body_html,
    isUnread: !e.is_read,
    hasAttachments: e.has_attachments,
    attachments: e.attachments,
  };
}

export default function Email() {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const socketCtx = useSocketOptional();

  const [activeFolder, setActiveFolder] = useState<FolderType>('inbox');
  const [composeOpen, setComposeOpen] = useState(false);
  const [mobileFoldersOpen, setMobileFoldersOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyData, setReplyData] = useState<{
    threadId: string;
    to: string;
    subject: string;
    originalBody?: string;
  } | null>(null);

  const [unifiedEmails, setUnifiedEmails] = useState<UnifiedEmail[]>([]);
  const [sentEmails, setSentEmails] = useState<UnifiedEmail[]>([]);
  const [trashEmails, setTrashEmails] = useState<UnifiedEmail[]>([]);
  const [archiveEmails, setArchiveEmails] = useState<UnifiedEmail[]>([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [loadingSent, setLoadingSent] = useState(false);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);

  const [selectedEmail, setSelectedEmail] = useState<UnifiedEmail | null>(null);
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [unifiedAccounts, setUnifiedAccounts] = useState<UnifiedEmailAccount[]>([]);
  // Task #896: traccia se l'elenco degli account è già stato caricato dal
  // backend. Serve a evitare di gating le chiamate Gmail (`hasGmailAccount`)
  // quando lo stato è ancora vuoto al primo render — altrimenti l'utente
  // Gmail vedrebbe la inbox vuota fino al primo cambio di cartella.
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('all');
  const [unreadCount, setUnreadCount] = useState(0);

  const [page, setPage] = useState(0);
  const [totalEmails, setTotalEmails] = useState(0);
  const PAGE_SIZE = 30;

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', subject: '', body_html: '', signature_html: '', is_default: false,
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [variableCategories, setVariableCategories] = useState<VariableCategory[]>([]);
  const [varPickerOpen, setVarPickerOpen] = useState(false);
  const [varPickerTarget, setVarPickerTarget] = useState<'subject' | 'body_html'>('body_html');
  const bodyEditorRef = useRef<RichTextEditorRef>(null);
  const [expandedVarCats, setExpandedVarCats] = useState<Set<string>>(new Set());
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [gmailTokenExpired, setGmailTokenExpired] = useState(false);

  const selectedUnified = unifiedAccounts.find(a => a.id === selectedAccountId);
  const googleAccountIdForApi = selectedUnified?.account_type === 'gmail' ? selectedUnified.google_account_id : undefined;

  const loadUnifiedAccounts = async () => {
    try {
      const res = await apiClient.get<{ data?: UnifiedEmailAccount[] }>('/email-accounts/all');
      if (res.success) {
        const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setUnifiedAccounts(list);
      }
    } catch (err) {
      console.error('Error loading unified email accounts:', err);
    } finally {
      // Task #896: marca sempre `accountsLoaded=true` (anche su errore) così
      // la pagina può procedere con la decisione su Gmail/IMAP. In caso di
      // errore restiamo con la lista vuota e Gmail viene saltato — coerente
      // con il fatto che l'utente non ha account caricati.
      setAccountsLoaded(true);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await apiClient.get<number | { count?: number }>('/email-accounts/inbox/unread-count');
      if (res.success && res.data !== undefined) {
        const count = typeof res.data === 'number' ? res.data : (res.data?.count || 0);
        setUnreadCount(count);
      }
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const loadVariableCategories = async () => {
    try {
      const res = await apiClient.get<VariableCategory[]>('/messaging/variables?scope=contact');
      if (res.success && res.data) setVariableCategories(res.data);
    } catch (err) {
      console.error('Failed to load variables:', err);
    }
  };

  useEffect(() => {
    loadUnifiedAccounts();
    loadVariableCategories();
    loadUnreadCount();
  }, []);

  // Task #896: helper centralizzato per capire se l'utente ha effettivamente
  // account Gmail collegati. Quando non ne ha, le chiamate `/email/messages`
  // vengono saltate (no toast rosso "Errore Gmail — Nessun account Google
  // configurato") e ci si limita ai soli account IMAP.
  const hasGmailAccount = unifiedAccounts.some(a => a.account_type === 'gmail');

  // Riconosce gli errori "informativi" lato Gmail che non devono essere
  // mostrati come toast: nessun account configurato (NO_GOOGLE_ACCOUNT).
  const isNoGoogleAccountError = (err: unknown): boolean => {
    const e = err as { code?: string; error_code?: string; data?: { error_code?: string }; message?: string } | null | undefined;
    const code = e?.code || e?.error_code || e?.data?.error_code;
    if (code === 'NO_GOOGLE_ACCOUNT') return true;
    const msg: string = String(e?.message || '');
    return msg.includes('Nessun account Google configurato');
  };

  const loadInbox = useCallback(async (pageNum?: number, query?: string) => {
    setLoadingInbox(true);
    try {
      const currentPage = pageNum !== undefined ? pageNum : page;
      const q = query !== undefined ? query : searchQuery;
      const hasAccountFilter = selectedAccountId !== 'all';
      const isSmtpAccount = selectedUnified?.account_type === 'smtp';
      const isGmailAccount = selectedUnified?.account_type === 'gmail';
      const allEmails: UnifiedEmail[] = [];

      // Task #896: salta del tutto la chiamata Gmail quando l'utente non ha
      // account Google: non ha senso interrogare l'API e mostrare un errore.
      const shouldLoadGmail = (!hasAccountFilter && hasGmailAccount) || isGmailAccount;
      const shouldLoadImap = !hasAccountFilter || isSmtpAccount;

      if (shouldLoadGmail) {
        try {
          const acctParam = googleAccountIdForApi ? `&account_id=${googleAccountIdForApi}` : '';
          let endpoint = `/email/messages?label=INBOX&max_results=${PAGE_SIZE}${acctParam}`;
          if (q.trim()) endpoint += `&q=${encodeURIComponent(q.trim())}`;
          const res = await apiClient.get<{ messages: EmailMessage[]; resultSizeEstimate: number }>(endpoint);
          // Task #896: il backend ora ritorna 200 + error_code='NO_GOOGLE_ACCOUNT'
          // quando non ci sono account Google: lo trattiamo come "nessun
          // messaggio Gmail" silenziosamente.
          const errorCode = (res as { error_code?: string })?.error_code || (res.data as { error_code?: string } | undefined)?.error_code;
          if (errorCode === 'NO_GOOGLE_ACCOUNT') {
            // niente da fare
          } else if (res.success && res.data) {
            const msgs = Array.isArray(res.data.messages) ? res.data.messages : Array.isArray(res.data) ? (res.data as unknown as EmailMessage[]) : [];
            msgs.forEach((m: EmailMessage) => {
              allEmails.push({
                id: `gmail_${m.id}`,
                source: 'gmail',
                accountId: googleAccountIdForApi || '',
                accountName: 'Gmail',
                accountEmail: '',
                accountType: 'gmail',
                threadId: m.threadId,
                from: m.from,
                fromName: extractName(m.from),
                to: m.to,
                cc: m.cc,
                subject: m.subject,
                snippet: m.snippet,
                date: m.date,
                bodyText: m.bodyText,
                bodyHtml: m.bodyHtml,
                isUnread: m.labelIds?.includes('UNREAD') || false,
                hasAttachments: false,
                labelIds: m.labelIds,
              });
            });
          }
        } catch (err) {
          console.error('Gmail load error:', err);
          if (isNoGoogleAccountError(err)) {
            // silenzioso: l'utente non ha Gmail, è un caso atteso.
          } else {
            const msg: string = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || '';
            if (msg.includes('Sessione Google scaduta') || msg.includes('invalid_grant') || msg.includes('GMAIL_TOKEN_EXPIRED')) {
              setGmailTokenExpired(true);
            } else {
              toast({ title: 'Errore Gmail', description: msg || 'Impossibile caricare le email', variant: 'destructive' });
            }
          }
        }
      }

      if (shouldLoadImap) {
        try {
          // Task #896: usa il filtro per `folderType=inbox` (nuovo) così la
          // INBOX include i record sincronizzati con SPECIAL-USE \Inbox e i
          // record legacy (pre-#896) che hanno folder_type NULL.
          let imapEndpoint = `/email-accounts/inbox/list?folderType=inbox&limit=${PAGE_SIZE}&offset=${currentPage * PAGE_SIZE}`;
          if (hasAccountFilter && isSmtpAccount) imapEndpoint += `&accountId=${selectedAccountId}`;
          if (q.trim()) imapEndpoint += `&search=${encodeURIComponent(q.trim())}`;
          const res = await apiClient.get<{ data?: ImapEmail[]; total?: number }>(imapEndpoint);
          if (res.success) {
            const responseData = res.data;
            const emails: ImapEmail[] = Array.isArray(responseData) ? responseData : (responseData?.data || []);
            const total = responseData?.total || emails.length;
            setTotalEmails(total);
            emails.forEach((e: ImapEmail) => {
              allEmails.push({
                id: `imap_${e.id}`,
                source: 'imap',
                accountId: e.account_id,
                accountName: e.account_name || 'IMAP',
                accountEmail: e.account_email || '',
                accountType: e.account_type || 'smtp',
                threadKey: e.thread_key || null,
                from: e.from_name ? `${e.from_name} <${e.from_address}>` : e.from_address,
                fromName: e.from_name || e.from_address || '',
                to: e.to_address,
                cc: e.cc || '',
                subject: e.subject,
                snippet: (e.body_text || '').substring(0, 120),
                date: e.date,
                bodyText: e.body_text,
                bodyHtml: e.body_html,
                isUnread: !e.is_read,
                hasAttachments: e.has_attachments,
                attachments: e.attachments,
              });
            });
          }
        } catch (err) {
          console.error('IMAP load error:', err);
        }
      }

      // Task #896: raggruppa per thread (Gmail per threadId nativo, IMAP per
      // thread_key). Mostra solo la testa di ogni conversazione.
      setUnifiedEmails(groupEmailsByThread(allEmails));
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore caricamento posta in arrivo', variant: 'destructive' });
    } finally {
      setLoadingInbox(false);
    }
  }, [page, searchQuery, selectedAccountId, googleAccountIdForApi, selectedUnified, hasGmailAccount, toast]);

  const loadSent = useCallback(async (query?: string) => {
    setLoadingSent(true);
    try {
      const q = query !== undefined ? query : searchQuery;
      const hasAccountFilter = selectedAccountId !== 'all';
      const isSmtpAccount = selectedUnified?.account_type === 'smtp';
      const isGmailAccount = selectedUnified?.account_type === 'gmail';
      const allEmails: UnifiedEmail[] = [];

      // Task #896: salta Gmail se non ci sono account Google.
      const shouldLoadGmail = (!hasAccountFilter && hasGmailAccount) || isGmailAccount;
      const shouldLoadImap = !hasAccountFilter || isSmtpAccount;

      if (shouldLoadGmail) {
        try {
          const acctParam = googleAccountIdForApi ? `&account_id=${googleAccountIdForApi}` : '';
          let endpoint = `/email/messages?label=SENT&max_results=${PAGE_SIZE}${acctParam}`;
          if (q.trim()) endpoint += `&q=${encodeURIComponent(q.trim())}`;
          const res = await apiClient.get<{ messages: EmailMessage[]; resultSizeEstimate: number }>(endpoint);
          const errorCode = (res as { error_code?: string })?.error_code || (res.data as { error_code?: string } | undefined)?.error_code;
          if (errorCode !== 'NO_GOOGLE_ACCOUNT' && res.success && res.data) {
            const msgs = Array.isArray(res.data.messages) ? res.data.messages : Array.isArray(res.data) ? (res.data as unknown as EmailMessage[]) : [];
            allEmails.push(...msgs.map((m: EmailMessage) => ({
              id: `gmail_${m.id}`,
              source: 'gmail' as const,
              accountId: googleAccountIdForApi || '',
              accountName: 'Gmail',
              accountEmail: '',
              accountType: 'gmail',
              threadId: m.threadId,
              from: m.from,
              fromName: extractName(m.from),
              to: m.to,
              cc: m.cc,
              subject: m.subject,
              snippet: m.snippet,
              date: m.date,
              bodyText: m.bodyText,
              bodyHtml: m.bodyHtml,
              isUnread: false,
              hasAttachments: false,
              labelIds: m.labelIds,
            })));
          }
        } catch (err) {
          if (!isNoGoogleAccountError(err)) {
            console.error('Gmail sent load error:', err);
          }
        }
      }

      // Task #896: nuova branca IMAP per la cartella Sent (popolata dalla
      // sincronizzazione SPECIAL-USE \Sent).
      if (shouldLoadImap) {
        try {
          const acctFilter = hasAccountFilter && isSmtpAccount ? `&accountId=${selectedAccountId}` : '';
          let imapEndpoint = `/email-accounts/inbox/list?folderType=sent&limit=${PAGE_SIZE}${acctFilter}`;
          if (q.trim()) imapEndpoint += `&search=${encodeURIComponent(q.trim())}`;
          const res = await apiClient.get<{ data?: ImapEmail[]; total?: number }>(imapEndpoint);
          if (res.success) {
            const responseData = res.data;
            const emails: ImapEmail[] = Array.isArray(responseData) ? responseData : (responseData?.data || []);
            allEmails.push(...emails.map((e: ImapEmail) => ({
              id: `imap_${e.id}`,
              source: 'imap' as const,
              accountId: e.account_id,
              accountName: e.account_name || 'IMAP',
              accountEmail: e.account_email || '',
              accountType: e.account_type || 'smtp',
              threadKey: e.thread_key || null,
              from: e.from_name ? `${e.from_name} <${e.from_address}>` : e.from_address,
              fromName: e.from_name || e.from_address || '',
              to: e.to_address,
              cc: e.cc || '',
              subject: e.subject,
              snippet: (e.body_text || '').substring(0, 120),
              date: e.date,
              bodyText: e.body_text,
              bodyHtml: e.body_html,
              isUnread: !e.is_read,
              hasAttachments: e.has_attachments,
              attachments: e.attachments,
            })));
          }
        } catch (err) {
          console.error('IMAP sent load error:', err);
        }
      }

      setSentEmails(groupEmailsByThread(allEmails));
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore caricamento email inviate', variant: 'destructive' });
    } finally {
      setLoadingSent(false);
    }
  }, [googleAccountIdForApi, hasGmailAccount, searchQuery, selectedAccountId, selectedUnified?.account_type, toast]);

  const loadTrash = useCallback(async (query?: string) => {
    setLoadingTrash(true);
    try {
      const q = query !== undefined ? query : searchQuery;
      const allEmails: UnifiedEmail[] = [];
      const hasAccountFilter = selectedAccountId !== 'all';
      const isSmtpAccount = selectedUnified?.account_type === 'smtp';
      const isGmailAccount = selectedUnified?.account_type === 'gmail';

      // Task #896: salta Gmail se non ci sono account Google.
      const shouldLoadGmail = (!hasAccountFilter && hasGmailAccount) || isGmailAccount;
      if (shouldLoadGmail) {
        try {
          const acctParam = googleAccountIdForApi ? `&account_id=${googleAccountIdForApi}` : '';
          let trashEndpoint = `/email/messages?label=TRASH&max_results=${PAGE_SIZE}${acctParam}`;
          if (q.trim()) trashEndpoint += `&q=${encodeURIComponent(q.trim())}`;
          const res = await apiClient.get<{ messages: EmailMessage[] }>(trashEndpoint);
          const errorCode = (res as { error_code?: string })?.error_code || (res.data as { error_code?: string } | undefined)?.error_code;
          if (errorCode !== 'NO_GOOGLE_ACCOUNT' && res.success && res.data) {
            const msgs = Array.isArray(res.data.messages) ? res.data.messages : [];
            allEmails.push(...msgs.map((m: EmailMessage) => ({
              id: `gmail_${m.id}`,
              source: 'gmail' as const,
              accountId: googleAccountIdForApi || '',
              accountName: 'Gmail',
              accountEmail: '',
              accountType: 'gmail',
              threadId: m.threadId,
              from: m.from,
              fromName: extractName(m.from),
              to: m.to,
              cc: m.cc,
              subject: m.subject,
              snippet: m.snippet,
              date: m.date,
              bodyText: m.bodyText,
              bodyHtml: m.bodyHtml,
              isUnread: m.labelIds?.includes('UNREAD') || false,
              hasAttachments: false,
              labelIds: m.labelIds,
            })));
          }
        } catch {}
      }

      if (!hasAccountFilter || isSmtpAccount) {
        try {
          const acctFilter = hasAccountFilter && isSmtpAccount ? `&accountId=${selectedAccountId}` : '';
          // Task #896: due fonti per il cestino IMAP:
          //   1) la cartella Trash sincronizzata via SPECIAL-USE \Trash;
          //   2) i messaggi soft-deletati localmente (filter=deleted), per
          //      retrocompatibilità con i record già presenti.
          const [resTrashFolder, resLegacy] = await Promise.all([
            apiClient.get<{ data?: ImapEmail[] }>(`/email-accounts/inbox/list?folderType=trash&limit=${PAGE_SIZE}${acctFilter}`),
            apiClient.get<{ data?: ImapEmail[] }>(`/email-accounts/inbox/list?filter=deleted&limit=${PAGE_SIZE}${acctFilter}`),
          ]);
          const collected: ImapEmail[] = [];
          for (const r of [resTrashFolder, resLegacy]) {
            if (r?.success && r.data) {
              const data = r.data;
              const arr: ImapEmail[] = Array.isArray(data) ? data : (data?.data || []);
              collected.push(...arr);
            }
          }
          // Deduplica per id (un messaggio potrebbe essere sia soft-deleted
          // localmente sia presente nella cartella Trash IMAP).
          const seen = new Set<string>();
          for (const e of collected) {
            if (seen.has(e.id)) continue;
            seen.add(e.id);
            allEmails.push({
              id: `imap_${e.id}`,
              source: 'imap' as const,
              accountId: e.account_id,
              accountName: e.account_name || '',
              accountEmail: e.account_email || '',
              accountType: e.account_type || 'smtp',
              threadKey: e.thread_key || null,
              from: e.from_name ? `${e.from_name} <${e.from_address}>` : e.from_address,
              fromName: e.from_name || e.from_address,
              to: e.to_address,
              cc: e.cc || '',
              subject: e.subject,
              snippet: (e.body_text || '').substring(0, 120),
              date: e.date,
              bodyText: e.body_text,
              bodyHtml: e.body_html,
              isUnread: !e.is_read,
              hasAttachments: e.has_attachments,
              attachments: e.attachments,
            });
          }
        } catch {}
      }

      let filtered = allEmails;
      if (q.trim()) {
        const lower = q.trim().toLowerCase();
        filtered = allEmails.filter(e =>
          (e.subject || '').toLowerCase().includes(lower) ||
          (e.from || '').toLowerCase().includes(lower) ||
          (e.to || '').toLowerCase().includes(lower) ||
          (e.snippet || '').toLowerCase().includes(lower)
        );
      }
      setTrashEmails(groupEmailsByThread(filtered));
    } finally {
      setLoadingTrash(false);
    }
  }, [googleAccountIdForApi, hasGmailAccount, searchQuery, selectedAccountId, selectedUnified?.account_type]);

  const loadArchive = useCallback(async (query?: string) => {
    setLoadingArchive(true);
    try {
      const q = query !== undefined ? query : searchQuery;
      const allEmails: UnifiedEmail[] = [];
      const hasAccountFilter = selectedAccountId !== 'all';
      const isSmtpAccount = selectedUnified?.account_type === 'smtp';
      const isGmailAccount = selectedUnified?.account_type === 'gmail';

      // Task #896: salta Gmail se non ci sono account Google.
      const shouldLoadGmail = (!hasAccountFilter && hasGmailAccount) || isGmailAccount;
      if (shouldLoadGmail) {
        try {
          const acctParam = googleAccountIdForApi ? `&account_id=${googleAccountIdForApi}` : '';
          let archiveQ = '-in:inbox -in:sent -in:trash -in:draft';
          if (q.trim()) archiveQ += ` ${q.trim()}`;
          const archiveQuery = encodeURIComponent(archiveQ);
          const res = await apiClient.get<{ messages: EmailMessage[] }>(`/email/messages?query=${archiveQuery}&max_results=${PAGE_SIZE}${acctParam}`);
          const errorCode = (res as { error_code?: string })?.error_code || (res.data as { error_code?: string } | undefined)?.error_code;
          if (errorCode !== 'NO_GOOGLE_ACCOUNT' && res.success && res.data) {
            const msgs = Array.isArray(res.data.messages) ? res.data.messages : [];
            allEmails.push(...msgs.map((m: EmailMessage) => ({
              id: `gmail_${m.id}`,
              source: 'gmail' as const,
              accountId: googleAccountIdForApi || '',
              accountName: 'Gmail',
              accountEmail: '',
              accountType: 'gmail',
              threadId: m.threadId,
              from: m.from,
              fromName: extractName(m.from),
              to: m.to,
              cc: m.cc,
              subject: m.subject,
              snippet: m.snippet,
              date: m.date,
              bodyText: m.bodyText,
              bodyHtml: m.bodyHtml,
              isUnread: m.labelIds?.includes('UNREAD') || false,
              hasAttachments: false,
              labelIds: m.labelIds,
            })));
          }
        } catch {}
      }

      if (!hasAccountFilter || isSmtpAccount) {
        try {
          const acctFilter = hasAccountFilter && isSmtpAccount ? `&accountId=${selectedAccountId}` : '';
          // Task #896: due fonti per l'archivio IMAP, identica logica del cestino:
          //   1) cartella Archive del server (SPECIAL-USE \Archive);
          //   2) messaggi archiviati localmente (filter=archived).
          const [resArchiveFolder, resLegacy] = await Promise.all([
            apiClient.get<{ data?: ImapEmail[] }>(`/email-accounts/inbox/list?folderType=archive&limit=${PAGE_SIZE}${acctFilter}`),
            apiClient.get<{ data?: ImapEmail[] }>(`/email-accounts/inbox/list?filter=archived&limit=${PAGE_SIZE}${acctFilter}`),
          ]);
          const collected: ImapEmail[] = [];
          for (const r of [resArchiveFolder, resLegacy]) {
            if (r?.success && r.data) {
              const data = r.data;
              const arr: ImapEmail[] = Array.isArray(data) ? data : (data?.data || []);
              collected.push(...arr);
            }
          }
          const seen = new Set<string>();
          for (const e of collected) {
            if (seen.has(e.id)) continue;
            seen.add(e.id);
            allEmails.push({
              id: `imap_${e.id}`,
              source: 'imap' as const,
              accountId: e.account_id,
              accountName: e.account_name || '',
              accountEmail: e.account_email || '',
              accountType: e.account_type || 'smtp',
              threadKey: e.thread_key || null,
              from: e.from_name ? `${e.from_name} <${e.from_address}>` : e.from_address,
              fromName: e.from_name || e.from_address,
              to: e.to_address,
              cc: e.cc || '',
              subject: e.subject,
              snippet: (e.body_text || '').substring(0, 120),
              date: e.date,
              bodyText: e.body_text,
              bodyHtml: e.body_html,
              isUnread: !e.is_read,
              hasAttachments: e.has_attachments,
              attachments: e.attachments,
            });
          }
        } catch {}
      }

      let filtered = allEmails;
      if (q.trim()) {
        const lower = q.trim().toLowerCase();
        filtered = allEmails.filter(e =>
          (e.subject || '').toLowerCase().includes(lower) ||
          (e.from || '').toLowerCase().includes(lower) ||
          (e.to || '').toLowerCase().includes(lower) ||
          (e.snippet || '').toLowerCase().includes(lower)
        );
      }
      setArchiveEmails(groupEmailsByThread(filtered));
    } finally {
      setLoadingArchive(false);
    }
  }, [googleAccountIdForApi, hasGmailAccount, searchQuery, selectedAccountId, selectedUnified?.account_type]);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await apiClient.get<EmailTemplate[]>('/email/templates');
      if (res.success && res.data) setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore caricamento template', variant: 'destructive' });
    } finally {
      setLoadingTemplates(false);
    }
  }, [toast]);

  useEffect(() => {
    setPage(0);
    setSelectedEmail(null);
    setSelectedThread(null);
    setSelectedIds(new Set());
    // Task #896: la cartella "templates" non dipende dall'elenco account,
    // quindi può caricarsi subito; per le cartelle di posta aspettiamo
    // l'esito di loadUnifiedAccounts() (vedi `accountsLoaded`) altrimenti
    // `hasGmailAccount` sarebbe false al primo render anche per gli utenti
    // Gmail e la pagina apparirebbe vuota fino al cambio di cartella.
    if (activeFolder === 'templates') {
      loadTemplates();
      return;
    }
    if (!accountsLoaded) return;
    if (activeFolder === 'inbox') {
      loadInbox(0);
    } else if (activeFolder === 'sent') {
      loadSent();
    } else if (activeFolder === 'trash') {
      loadTrash();
    } else if (activeFolder === 'archive') {
      loadArchive();
    }
  }, [activeFolder, selectedAccountId, accountsLoaded, loadArchive, loadInbox, loadSent, loadTemplates, loadTrash]);

  // Task #915: ascolta `email:moved` emesso dal backend dopo le rotte
  // archive / unarchive / soft-delete / restore. Aggiorna in tempo reale
  // tutti i tab aperti dello stesso utente: rimozione dalla cartella di
  // partenza, comparsa in quella di destinazione e contatori non-lette
  // allineati senza dover lanciare un nuovo sync. L'evento è emesso sulla
  // room `user:${userId}` quindi è privato dell'utente loggato.
  useEffect(() => {
    if (!socketCtx) return;
    const { on, off } = socketCtx;

    // Task #915: rispetta il filtro account attivo. Quando la pagina è
    // filtrata su un account specifico, gli eventi di altri account NON
    // devono modificare le liste — altrimenti vedremmo righe "fantasma"
    // appartenenti ad account che l'utente sta volutamente nascondendo.
    const eventMatchesActiveAccount = (data: EmailMovedEvent): boolean => {
      if (selectedAccountId === 'all') return true;
      if (!selectedUnified) return false;
      if (data.source === 'imap') {
        // Per IMAP confrontiamo l'id locale dell'account
        // (tenant_email_accounts.id) restituito dal backend.
        return selectedUnified.account_type === 'smtp'
          && data.account_id === selectedAccountId;
      }
      // Gmail: confrontiamo con `googleAccountIdForApi` perché è ciò che
      // il frontend invia come `account_id` nelle rotte di azione.
      return selectedUnified.account_type === 'gmail'
        && data.account_id === googleAccountIdForApi;
    };

    const handleEmailMoved = (data: EmailMovedEvent) => {
      if (!data || !data.email_id) return;
      if (!eventMatchesActiveAccount(data)) return;

      const unifiedId = data.source === 'imap'
        ? `imap_${data.email_id}`
        : `gmail_${data.email_id}`;

      // 1) Rimozione dalla cartella di partenza. Per `sent` e `drafts` non
      //    facciamo nulla di particolare: rimuoviamo solo dalla lista
      //    dedicata se ne fa parte.
      const removeFromList = (
        setter: React.Dispatch<React.SetStateAction<UnifiedEmail[]>>,
      ) => setter(prev => prev.filter(e => e.id !== unifiedId));

      switch (data.from_folder_type) {
        case 'inbox':
          removeFromList(setUnifiedEmails);
          break;
        case 'archive':
          removeFromList(setArchiveEmails);
          break;
        case 'trash':
          removeFromList(setTrashEmails);
          break;
        case 'sent':
          removeFromList(setSentEmails);
          break;
        default:
          // from sconosciuto: rimuoviamo da tutte le liste per sicurezza,
          // così nessun tab resta con una riga "fantasma".
          removeFromList(setUnifiedEmails);
          removeFromList(setArchiveEmails);
          removeFromList(setTrashEmails);
          break;
      }

      // 2) Comparsa nella cartella di destinazione (solo IMAP, perché per
      //    Gmail non abbiamo il record completo lato backend).
      if (data.source === 'imap' && data.email) {
        const unified = imapRowToUnified(data.email);
        const upsert = (
          setter: React.Dispatch<React.SetStateAction<UnifiedEmail[]>>,
        ) => setter(prev => {
          if (prev.some(e => e.id === unified.id)) return prev;
          const next = [unified, ...prev];
          next.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          return next;
        });
        switch (data.to_folder_type) {
          case 'inbox':
            upsert(setUnifiedEmails);
            break;
          case 'archive':
            upsert(setArchiveEmails);
            break;
          case 'trash':
            upsert(setTrashEmails);
            break;
        }
      }

      // 3) Aggiornamento del contatore non-lette globale: scala se la mail
      //    lascia la inbox da non letta, incrementa se torna in inbox da
      //    non letta. (Stesso campo usato dalla notifica di posta nuova.)
      if (data.was_unread) {
        if (data.from_folder_type === 'inbox' && data.to_folder_type !== 'inbox') {
          setUnreadCount(prev => Math.max(0, prev - 1));
        } else if (data.to_folder_type === 'inbox' && data.from_folder_type !== 'inbox') {
          setUnreadCount(prev => prev + 1);
        }
      }

      // 4) Se la mail attualmente aperta è stata spostata in cestino o
      //    archivio, chiudiamo il dettaglio così l'utente non vede uno
      //    stato incoerente.
      setSelectedEmail(prev => {
        if (prev && prev.id === unifiedId &&
            (data.to_folder_type === 'trash' || data.to_folder_type === 'archive') &&
            activeFolder !== data.to_folder_type) {
          setSelectedThread(null);
          return null;
        }
        return prev;
      });
    };

    on('email:moved', handleEmailMoved);
    return () => {
      off('email:moved', handleEmailMoved);
    };
    // socketCtx.on/off sono callback stabili (useCallback nel provider) ma
    // includiamo `socketCtx` per ri-attaccarci se cambia identity (es. dopo
    // un refresh token che ricostruisce il socket). Le dipendenze su
    // selectedAccountId / selectedUnified / googleAccountIdForApi servono
    // a far sì che il filtro per account rifletta la selezione corrente.
  }, [socketCtx, activeFolder, selectedAccountId, selectedUnified, googleAccountIdForApi]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const smtpAccounts = unifiedAccounts.filter(a => a.account_type === 'smtp');
      // Task #896: usa il nuovo endpoint sync-all che scopre e sincronizza
      // anche le cartelle Sent/Archive/Trash (oltre alla INBOX) per ogni
      // account IMAP.
      const syncPromises = smtpAccounts.map(async (acct) => {
        try {
          await apiClient.post(`/email-accounts/${acct.id}/sync-all`, { limit: 50 });
        } catch (err) {
          console.error(`Sync error for ${acct.display_name}:`, err);
        }
      });
      await Promise.all(syncPromises);
      toast({ title: 'Sincronizzazione completata', description: 'Email IMAP sincronizzate' });
      // Ricarica la cartella attualmente visualizzata.
      if (activeFolder === 'inbox') loadInbox(0);
      else if (activeFolder === 'sent') loadSent();
      else if (activeFolder === 'archive') loadArchive();
      else if (activeFolder === 'trash') loadTrash();
      loadUnreadCount();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore sincronizzazione', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleEmailClick = async (email: UnifiedEmail) => {
    setSelectedEmail(email);
    setSelectedThread(null);

    if (email.source === 'gmail' && email.threadId) {
      setLoadingDetail(true);
      try {
        const acctParam = googleAccountIdForApi ? `?account_id=${googleAccountIdForApi}` : '';
        const res = await apiClient.get<EmailThread>(`/email/threads/${email.threadId}${acctParam}`);
        if (res.success && res.data) setSelectedThread(res.data);
      } catch (err) {
        console.error('Thread load error:', err);
      } finally {
        setLoadingDetail(false);
      }
      if (email.isUnread) {
        try {
          const realId = email.id.replace('gmail_', '');
          const acctParam = googleAccountIdForApi ? `?account_id=${googleAccountIdForApi}` : '';
          await apiClient.post(`/email/messages/${realId}/read${acctParam}`);
          setUnifiedEmails(prev => prev.map(e => e.id === email.id ? { ...e, isUnread: false } : e));
          setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
          console.error('Mark read error:', err);
        }
      }
    } else if (email.source === 'imap') {
      // Task #896: se l'email IMAP appartiene a un thread con più messaggi
      // (oppure ha una thread_key del server), recuperiamo l'intera
      // conversazione per popolare il pannello di dettaglio.
      const shouldLoadThread = (email.threadCount && email.threadCount > 1) || !!email.threadKey;
      if (shouldLoadThread && email.threadKey) {
        setLoadingDetail(true);
        try {
          const url = `/email-accounts/inbox/thread/by-key?accountId=${encodeURIComponent(email.accountId)}&threadKey=${encodeURIComponent(email.threadKey)}`;
          const res = await apiClient.get<{ data?: ImapEmail[] }>(url);
          if (res.success && res.data) {
            const arr: ImapEmail[] = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            // Mappa nel formato EmailMessage atteso dal pannello dettaglio.
            const messages: EmailMessage[] = arr
              .slice()
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .map((m) => ({
                id: m.id,
                threadId: m.thread_key || '',
                from: m.from_name ? `${m.from_name} <${m.from_address}>` : m.from_address,
                to: m.to_address,
                cc: m.cc || '',
                subject: m.subject,
                snippet: (m.body_text || '').substring(0, 200),
                bodyText: m.body_text,
                bodyHtml: m.body_html,
                date: m.date,
                labelIds: [],
                internalDate: m.date,
              }));
            if (messages.length > 0) {
              setSelectedThread({
                threadId: email.threadKey,
                messages,
              });
            }
          }
        } catch (err) {
          console.error('IMAP thread load error:', err);
        } finally {
          setLoadingDetail(false);
        }
      }
      if (email.isUnread) {
        try {
          const realId = email.id.replace('imap_', '');
          await apiClient.put(`/email-accounts/inbox/${realId}/read`, { is_read: true });
          setUnifiedEmails(prev => prev.map(e => e.id === email.id ? { ...e, isUnread: false } : e));
          setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
          console.error('Mark read error:', err);
        }
      }
    }
  };

  const handleDeleteEmail = async (email: UnifiedEmail, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDeletingMessageId(email.id);
    try {
      const realId = email.id.replace(/^(gmail_|imap_)/, '');
      await apiClient.post(`/email/messages/${realId}/soft-delete`, {
        source: email.source,
        account_id: email.source === 'gmail' ? googleAccountIdForApi : undefined,
      });
      setUnifiedEmails(prev => prev.filter(e => e.id !== email.id));
      setSentEmails(prev => prev.filter(e => e.id !== email.id));
      setArchiveEmails(prev => prev.filter(e => e.id !== email.id));
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null);
        setSelectedThread(null);
      }
      toast({ title: 'Spostata nel cestino', description: 'Email spostata nel cestino' });
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Impossibile eliminare', variant: 'destructive' });
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleArchiveEmail = async (email: UnifiedEmail, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const realId = email.id.replace(/^(gmail_|imap_)/, '');
      await apiClient.post(`/email/messages/${realId}/archive`, {
        source: email.source,
        account_id: email.source === 'gmail' ? googleAccountIdForApi : undefined,
      });
      setUnifiedEmails(prev => prev.filter(e => e.id !== email.id));
      setSentEmails(prev => prev.filter(e => e.id !== email.id));
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null);
        setSelectedThread(null);
      }
      toast({ title: 'Archiviata', description: 'Email archiviata' });
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Impossibile archiviare', variant: 'destructive' });
    }
  };

  const handleRestoreEmail = async (email: UnifiedEmail, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const realId = email.id.replace(/^(gmail_|imap_)/, '');
      if (activeFolder === 'trash') {
        await apiClient.post(`/email/messages/${realId}/restore`, {
          source: email.source,
          account_id: email.source === 'gmail' ? googleAccountIdForApi : undefined,
        });
        setTrashEmails(prev => prev.filter(e => e.id !== email.id));
      } else if (activeFolder === 'archive') {
        await apiClient.post(`/email/messages/${realId}/unarchive`, {
          source: email.source,
          account_id: email.source === 'gmail' ? googleAccountIdForApi : undefined,
        });
        setArchiveEmails(prev => prev.filter(e => e.id !== email.id));
      }
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null);
        setSelectedThread(null);
      }
      toast({ title: 'Ripristinata', description: 'Email ripristinata' });
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Impossibile ripristinare', variant: 'destructive' });
    }
  };

  const handleMarkUnread = async (email: UnifiedEmail) => {
    try {
      if (email.source === 'imap') {
        const realId = email.id.replace('imap_', '');
        await apiClient.put(`/email-accounts/inbox/${realId}/read`, { is_read: false });
      } else if (email.source === 'gmail') {
        const realId = email.id.replace('gmail_', '');
        const acctParam = email.google_account_id ? `?account_id=${email.google_account_id}` : '';
        await apiClient.post(`/email/messages/${realId}/unread${acctParam}`);
      }
      setUnifiedEmails(prev => prev.map(e => e.id === email.id ? { ...e, isUnread: true } : e));
      setUnreadCount(prev => prev + 1);
    } catch (err) {
      console.error('Mark unread error:', err);
    }
  };

  const handleReply = (email: UnifiedEmail) => {
    setReplyData({
      threadId: email.threadId || email.id,
      to: email.from,
      subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      originalBody: email.bodyText,
    });
    setReplyOpen(true);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    if (activeFolder === 'inbox') {
      loadInbox(0, searchQuery);
    } else if (activeFolder === 'sent') {
      loadSent(searchQuery);
    } else if (activeFolder === 'trash') {
      loadTrash(searchQuery);
    } else if (activeFolder === 'archive') {
      loadArchive(searchQuery);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
    if (activeFolder === 'inbox') {
      loadInbox(0, '');
    } else if (activeFolder === 'sent') {
      loadSent('');
    } else if (activeFolder === 'trash') {
      loadTrash('');
    } else if (activeFolder === 'archive') {
      loadArchive('');
    }
  };

  const toggleSelectEmail = (emailId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(emailId)) next.delete(emailId); else next.add(emailId);
      return next;
    });
  };

  const currentEmails = activeFolder === 'inbox' ? unifiedEmails : activeFolder === 'sent' ? sentEmails : activeFolder === 'trash' ? trashEmails : activeFolder === 'archive' ? archiveEmails : [];

  const toggleSelectAll = () => {
    const currentEmails = activeFolder === 'inbox' ? unifiedEmails : sentEmails;
    if (selectedIds.size === currentEmails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentEmails.map(e => e.id)));
    }
  };

  const handleBatchDelete = async () => {
    const toDelete = Array.from(selectedIds);
    for (const id of toDelete) {
      const email = currentEmails.find(e => e.id === id);
      if (email) await handleDeleteEmail(email);
    }
    setSelectedIds(new Set());
  };

  const handleBatchArchive = async () => {
    const toArchive = Array.from(selectedIds);
    for (const id of toArchive) {
      const email = currentEmails.find(e => e.id === id);
      if (email) await handleArchiveEmail(email);
    }
    setSelectedIds(new Set());
  };

  const handleBatchMarkRead = async () => {
    for (const id of selectedIds) {
      const email = unifiedEmails.find(e => e.id === id);
      if (email && email.isUnread) {
        if (email.source === 'imap') {
          const realId = email.id.replace('imap_', '');
          try { await apiClient.put(`/email-accounts/inbox/${realId}/read`, { is_read: true }); } catch {}
        } else if (email.source === 'gmail') {
          const realId = email.id.replace('gmail_', '');
          const acctParam = googleAccountIdForApi ? `?account_id=${googleAccountIdForApi}` : '';
          try { await apiClient.post(`/email/messages/${realId}/read${acctParam}`); } catch {}
        }
      }
    }
    setUnifiedEmails(prev => prev.map(e => selectedIds.has(e.id) ? { ...e, isUnread: false } : e));
    setSelectedIds(new Set());
    loadUnreadCount();
  };

  const openTemplateForm = (template?: EmailTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name, subject: template.subject,
        body_html: template.body_html || '', signature_html: template.signature_html || '',
        is_default: template.is_default || false,
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body_html: '', signature_html: '', is_default: false });
    }
    setTemplateFormOpen(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim()) {
      toast({ title: 'Errore', description: 'Nome e oggetto sono obbligatori', variant: 'destructive' });
      return;
    }
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        const res = await apiClient.put(`/email/templates/${editingTemplate.id}`, templateForm);
        if (!res.success) throw new Error(res.error || 'Errore aggiornamento template');
        toast({ title: 'Template aggiornato', description: `Template "${templateForm.name}" aggiornato` });
      } else {
        const res = await apiClient.post('/email/templates', templateForm);
        if (!res.success) throw new Error(res.error || 'Errore creazione template');
        toast({ title: 'Template creato', description: `Template "${templateForm.name}" creato` });
      }
      setTemplateFormOpen(false);
      loadTemplates();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore salvataggio template', variant: 'destructive' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const res = await apiClient.delete(`/email/templates/${id}`);
      if (!res.success) throw new Error(res.error || 'Errore eliminazione template');
      toast({ title: 'Template eliminato', description: 'Il template eliminato' });
      setDeleteConfirmId(null);
      loadTemplates();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore eliminazione template', variant: 'destructive' });
    }
  };

  const insertVariable = (varKey: string) => {
    const tag = `{{${varKey}}}`;
    if (varPickerTarget === 'body_html' && bodyEditorRef.current) {
      bodyEditorRef.current.insertAtCursor(tag);
    } else {
      setTemplateForm(f => ({ ...f, [varPickerTarget]: f[varPickerTarget] + tag }));
    }
  };

  const toggleVarCat = (catId: string) => {
    setExpandedVarCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      return next;
    });
  };
  const currentLoading = activeFolder === 'inbox' ? loadingInbox : activeFolder === 'sent' ? loadingSent : activeFolder === 'trash' ? loadingTrash : activeFolder === 'archive' ? loadingArchive : false;

  const folderItems = [
    { id: 'inbox' as FolderType, label: 'Posta in Arrivo', icon: Inbox, count: unreadCount },
    { id: 'sent' as FolderType, label: 'Inviati', icon: SendHorizonal, count: 0 },
    { id: 'archive' as FolderType, label: 'Archivio', icon: Archive, count: 0 },
    { id: 'trash' as FolderType, label: 'Cestino', icon: Trash2, count: 0 },
  ];

  const renderFolderSidebar = () => (
    <div className="w-56 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col shrink-0">
      <div className="px-3 pt-3 pb-1">
        <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Account</p>
        <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Tutti" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli account</SelectItem>
            {unifiedAccounts.map(acct => (
              <SelectItem key={acct.id} value={acct.id}>
                <span className="flex items-center gap-1.5 text-xs">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${acct.account_type === 'gmail' ? 'bg-red-400' : 'bg-blue-400'}`} />
                  {acct.display_name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="p-3 pt-2">
        <Button onClick={() => setComposeOpen(true)} className="w-full" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Email
        </Button>
      </div>

      <div className="px-2 space-y-0.5">
        {folderItems.map(f => (
          <button
            key={f.id}
            onClick={() => setActiveFolder(f.id)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
              activeFolder === f.id
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <f.icon className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left truncate">{f.label}</span>
            {f.count > 0 && (
              <Badge variant={activeFolder === f.id ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center">
                {f.count}
              </Badge>
            )}
          </button>
        ))}
      </div>

      <div className="mt-auto p-3 border-t border-gray-200 dark:border-gray-700 space-y-1">
        <TooltipProvider>
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-xs text-gray-500 dark:text-gray-400"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  Sincronizza
                </Button>
              </TooltipTrigger>
              <TooltipContent>Scarica nuove email dai server IMAP</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeFolder === 'templates' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="text-xs text-gray-500 dark:text-gray-400 px-2"
                  onClick={() => setActiveFolder('templates')}
                >
                  <LayoutTemplate className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Template email ({templates.length})</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </div>
  );

  const renderEmailListItem = (email: UnifiedEmail, isSent: boolean = false) => {
    const isSelected = selectedEmail?.id === email.id;
    const isChecked = selectedIds.has(email.id);
    const displayName = isSent ? extractName(email.to) : email.fromName || extractName(email.from);
    const initials = getInitials(displayName);
    const avatarColor = getAvatarColor(displayName);

    return (
      <div
        key={email.id}
        onClick={() => handleEmailClick(email)}
        className={`group flex items-center gap-2.5 px-3 py-2.5 cursor-pointer border-b border-gray-100 dark:border-gray-700 transition-all ${
          isSelected ? 'bg-blue-50 dark:bg-blue-900/30 border-l-2 border-l-blue-500' : 'hover:bg-gray-50 dark:hover:bg-gray-800 border-l-2 border-l-transparent'
        } ${email.isUnread ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/30 dark:bg-gray-800/30'}`}
      >
        <div className="flex items-center shrink-0" onClick={(e) => { e.stopPropagation(); toggleSelectEmail(email.id); }}>
          <Checkbox checked={isChecked} className="h-4 w-4" />
        </div>

        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 ${avatarColor}`}>
          {initials}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`text-sm truncate ${email.isUnread ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
              {displayName}
            </span>
            {email.source === 'imap' && (
              <span className="text-[10px] px-1 py-0 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 shrink-0">IMAP</span>
            )}
            {email.source === 'gmail' && (
              <span className="text-[10px] px-1 py-0 rounded bg-red-100 dark:bg-red-900/30 text-red-600 shrink-0">Gmail</span>
            )}
            <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-auto shrink-0">{formatRelativeDate(email.date)}</span>
          </div>
          <p className={`text-[13px] truncate ${email.isUnread ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
            {email.subject || '(Nessun oggetto)'}
            {email.threadCount && email.threadCount > 1 && (
              <span className="ml-1.5 inline-flex items-center text-[10px] px-1 py-0 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 align-middle">
                {email.threadCount}
              </span>
            )}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{email.snippet}</p>
        </div>

        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {email.isUnread && <div className="w-2 h-2 rounded-full bg-blue-500" />}
          {email.hasAttachments && <Download className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />}
          {(activeFolder === 'trash' || activeFolder === 'archive') ? (
            <Button
              variant="ghost" size="sm"
              className="h-6 w-6 p-0 text-gray-400 dark:text-gray-500 hover:text-green-600"
              onClick={(e) => handleRestoreEmail(email, e)}
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
          ) : (
            <>
              {(activeFolder === 'inbox' || activeFolder === 'sent') && (
                <Button
                  variant="ghost" size="sm"
                  className="h-6 w-6 p-0 text-gray-400 dark:text-gray-500 hover:text-blue-500"
                  onClick={(e) => handleArchiveEmail(email, e)}
                >
                  <Archive className="w-3 h-3" />
                </Button>
              )}
              <Button
                variant="ghost" size="sm"
                className="h-6 w-6 p-0 text-gray-400 dark:text-gray-500 hover:text-red-500"
                onClick={(e) => handleDeleteEmail(email, e)}
                disabled={deletingMessageId === email.id}
              >
                {deletingMessageId === email.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const folderLabel = folderItems.find(f => f.id === activeFolder)?.label || (activeFolder === 'templates' ? 'Template' : 'Email');

  const EMPTY_STATE_COPY: Record<Exclude<FolderType, 'templates'>, { icon: LucideIcon; title: string; subtitle: string }> = {
    inbox: { icon: Inbox, title: 'La posta in arrivo è vuota', subtitle: 'Sincronizza per scaricare le nuove email dai tuoi account.' },
    sent: { icon: SendHorizonal, title: 'Nessuna email inviata', subtitle: 'Le email che invii compariranno qui.' },
    archive: { icon: Archive, title: "L'archivio è vuoto", subtitle: 'Le email archiviate vengono spostate qui.' },
    trash: { icon: Trash2, title: 'Il cestino è vuoto', subtitle: 'Le email eliminate restano qui prima della rimozione definitiva.' },
  };

  const renderEmptyState = () => {
    if (unifiedAccounts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-8 py-16 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 mb-4">
            <Mail className="w-8 h-8 text-blue-500/70 dark:text-blue-400/70" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Nessun account email configurato</h3>
          <p className="mt-1.5 max-w-xs text-sm text-gray-500 dark:text-gray-400">
            Collega una casella IMAP/SMTP o un account Gmail per iniziare a ricevere e inviare email.
          </p>
        </div>
      );
    }
    const copy = activeFolder === 'templates' ? EMPTY_STATE_COPY.inbox : EMPTY_STATE_COPY[activeFolder];
    const Icon = copy.icon;
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-8 py-16 text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 mb-4">
          <Icon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{copy.title}</h3>
        <p className="mt-1.5 max-w-xs text-sm text-gray-500 dark:text-gray-400">{copy.subtitle}</p>
        {activeFolder === 'inbox' && (
          <Button variant="outline" size="sm" className="mt-5" onClick={handleSync} disabled={syncing}>
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sincronizza
          </Button>
        )}
      </div>
    );
  };

  const renderEmailList = () => {
    const isSent = activeFolder === 'sent';

    return (
      <div className="flex flex-col h-full border-r border-gray-200 dark:border-gray-700" style={isMobile ? { width: '100%' } : { width: selectedEmail ? '360px' : '100%', minWidth: selectedEmail ? '360px' : undefined }}>
        {gmailTokenExpired && (
          <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border-b border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-800">Sessione Google scaduta</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Il token di accesso Gmail non è più valido. Vai nelle{' '}
                <button
                  onClick={() => { window.location.hash = '#settings'; }}
                  className="underline font-medium hover:text-amber-900"
                >
                  Impostazioni Email
                </button>
                {' '}→ Account Gmail → Disconnetti e riconnetti l'account Google.
              </p>
            </div>
            <button onClick={() => setGmailTokenExpired(false)} className="text-amber-500 hover:text-amber-700 text-xs shrink-0">✕</button>
          </div>
        )}
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{folderLabel}</h3>
          <span className="text-xs text-gray-400 dark:text-gray-500">{currentEmails.length} email</span>
        </div>
        <div className="p-2.5 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <form onSubmit={handleSearch} className="flex gap-1.5">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca email..."
                className="pl-8 h-8 text-sm"
              />
            </div>
            <Button type="submit" variant="outline" size="sm" className="h-8 px-2.5">
              <Search className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-8 px-2.5" onClick={handleClearSearch}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </form>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 px-1">
              <button onClick={toggleSelectAll} className="text-xs text-blue-600 hover:underline">
                {selectedIds.size === currentEmails.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500">({selectedIds.size} selezionati)</span>
              <div className="ml-auto flex gap-1">
                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={handleBatchMarkRead}>
                  <MailCheck className="w-3 h-3 mr-1" /> Letti
                </Button>
                {(activeFolder === 'inbox' || activeFolder === 'sent') && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs px-2 text-blue-500" onClick={handleBatchArchive}>
                    <Archive className="w-3 h-3 mr-1" /> Archivia
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-6 text-xs px-2 text-red-500" onClick={handleBatchDelete}>
                  <Trash2 className="w-3 h-3 mr-1" /> Elimina
                </Button>
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          {currentLoading || !accountsLoaded ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500 mr-2" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Caricamento...</span>
            </div>
          ) : currentEmails.length === 0 ? (
            renderEmptyState()
          ) : (
            <>
              {currentEmails.map(email => renderEmailListItem(email, isSent))}
              {activeFolder === 'inbox' && totalEmails > PAGE_SIZE && (
                <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100 dark:border-gray-700">
                  <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => { setPage(p => p - 1); loadInbox(page - 1); }}>
                    Prec.
                  </Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalEmails)} di {totalEmails}
                  </span>
                  <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE_SIZE >= totalEmails} onClick={() => { setPage(p => p + 1); loadInbox(page + 1); }}>
                    Succ.
                  </Button>
                </div>
              )}
            </>
          )}
        </ScrollArea>
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (!selectedEmail) {
      return null;
    }

    if (loadingDetail) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500 mr-2" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Caricamento...</span>
        </div>
      );
    }

    const messages = selectedThread?.messages || [];
    const singleEmail = messages.length === 0;
    const displayName = selectedEmail.fromName || extractName(selectedEmail.from);
    const senderEmail = extractEmail(selectedEmail.from);

    return (
      <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 min-w-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="shrink-0 lg:hidden" onClick={() => setSelectedEmail(null)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h3 className="font-medium text-gray-900 dark:text-white truncate">
              {selectedEmail.subject || '(Nessun oggetto)'}
            </h3>
            {messages.length > 1 && (
              <Badge variant="secondary" className="shrink-0">{messages.length}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleReply(selectedEmail)}>
                    <Reply className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Rispondi</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleMarkUnread(selectedEmail)}>
                    <MailX className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Segna non letto</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {(activeFolder === 'trash' || activeFolder === 'archive') ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-green-600" onClick={() => handleRestoreEmail(selectedEmail)}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Ripristina</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <>
                {(activeFolder === 'inbox' || activeFolder === 'sent') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleArchiveEmail(selectedEmail)}>
                          <Archive className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Archivia</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDeleteEmail(selectedEmail)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Elimina</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1">
          {singleEmail ? (
            <div className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0 ${getAvatarColor(displayName)}`}>
                  {getInitials(displayName)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{displayName}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">&lt;{senderEmail}&gt;</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">A: {selectedEmail.to}</p>
                  {selectedEmail.cc && <p className="text-xs text-gray-500 dark:text-gray-400">CC: {selectedEmail.cc}</p>}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{new Date(selectedEmail.date).toLocaleString('it-IT')}</p>
                  {selectedEmail.source === 'imap' && selectedEmail.accountName && (
                    <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600">
                      via {selectedEmail.accountName}
                    </span>
                  )}
                </div>
              </div>
              <Separator className="mb-4" />
              {selectedEmail.bodyHtml ? (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.bodyHtml) }}
                />
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedEmail.bodyText}</p>
              )}

              {selectedEmail.hasAttachments && selectedEmail.attachments && (() => {
                const rawAtts = selectedEmail.attachments;
                const atts: EmailAttachment[] = Array.isArray(rawAtts)
                  ? rawAtts
                  : typeof rawAtts === 'string'
                    ? (() => { try { return JSON.parse(rawAtts) as EmailAttachment[]; } catch { return []; } })()
                    : [];
                if (atts.length === 0) return null;
                return (
                  <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{atts.length} allegat{atts.length === 1 ? 'o' : 'i'}</span>
                    </div>
                    <div className="space-y-1.5">
                      {atts.map((att, i: number) => {
                        const isImage = att.contentType?.startsWith('image/');
                        const Icon = isImage ? Image : att.contentType?.includes('pdf') ? FileText : FileIcon;
                        const size = att.size ? (att.size < 1024 ? `${att.size} B` : att.size < 1024*1024 ? `${(att.size/1024).toFixed(1)} KB` : `${(att.size/(1024*1024)).toFixed(1)} MB`) : '';
                        return (
                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
                            <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">{att.filename || 'allegato'}</span>
                            {size && <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{size}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {messages.map((msg, idx) => {
                const msgName = extractName(msg.from);
                return (
                  <div key={msg.id || idx} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="flex items-start gap-3 p-4 pb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 ${getAvatarColor(msgName)}`}>
                        {getInitials(msgName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{msgName}</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">&lt;{extractEmail(msg.from)}&gt;</span>
                          </div>
                          <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">{formatRelativeDate(msg.date)}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">A: {msg.to}</p>
                        {msg.cc && <p className="text-xs text-gray-500 dark:text-gray-400">CC: {msg.cc}</p>}
                      </div>
                    </div>
                    <Separator />
                    <div className="p-4">
                      {msg.bodyHtml ? (
                        <div
                          className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.bodyHtml) }}
                        />
                      ) : (
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.bodyText}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="p-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <Button onClick={() => handleReply(selectedEmail)} className="w-full" size="sm">
            <Reply className="w-4 h-4 mr-2" />
            Rispondi
          </Button>
        </div>
      </div>
    );
  };

  const renderTemplatesPanel = () => (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-medium text-gray-900 dark:text-white">Template Email</h3>
        <Button size="sm" onClick={() => openTemplateForm()}>
          <Plus className="w-4 h-4 mr-1" />
          Nuovo
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {loadingTemplates ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500 mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Caricamento...</span>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FileText className="w-10 h-10 mb-2" />
            <p className="text-sm">Nessun template</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => openTemplateForm()}>
              Crea Template
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {templates.map(t => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800">
                <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{t.name}</span>
                    {t.is_default && <Badge variant="secondary" className="text-[10px]">Default</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{t.subject}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openTemplateForm(t)}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeleteConfirmId(t.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );

  return (
    <div className={cn("h-[calc(100vh-64px)] bg-white dark:bg-gray-900 overflow-hidden", isMobile ? "flex flex-col" : "flex")}>
      {!isMobile && renderFolderSidebar()}

      {isMobile && !selectedEmail && (
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50 dark:bg-gray-800 shrink-0">
          <Sheet open={mobileFoldersOpen} onOpenChange={setMobileFoldersOpen}>
            <SheetTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 px-2" aria-label="Apri cartelle email">
                <MenuIcon className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 max-w-[85vw]">
              <SheetHeader className="px-4 pt-4 pb-2">
                <SheetTitle>Cartelle email</SheetTitle>
              </SheetHeader>
              <div className="overflow-y-auto h-[calc(100%-3.5rem)]" onClick={() => setMobileFoldersOpen(false)}>
                {renderFolderSidebar()}
              </div>
            </SheetContent>
          </Sheet>
          <div className="flex-1 min-w-0 flex items-center gap-1.5 text-sm font-medium truncate">
            {(() => {
              const current = folderItems.find(f => f.id === activeFolder);
              if (!current) return null;
              const Icon = current.icon;
              return (
                <>
                  <Icon className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="truncate">{current.label}</span>
                  {current.count > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 ml-1">{current.count}</Badge>
                  )}
                </>
              );
            })()}
          </div>
          <Button size="sm" className="h-8 text-xs" onClick={() => setComposeOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />Nuova
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs px-2" onClick={handleSync} disabled={syncing} aria-label="Sincronizza">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      )}

      {activeFolder === 'templates' ? (
        renderTemplatesPanel()
      ) : (
        <>
          {(!isMobile || !selectedEmail) && renderEmailList()}
          {isMobile && selectedEmail ? (
            <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900">
              <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedEmail(null)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium truncate flex-1">{selectedEmail.subject || 'Email'}</span>
              </div>
              <div className="flex-1 overflow-hidden">
                {renderDetailPanel()}
              </div>
            </div>
          ) : (
            !isMobile && selectedEmail && renderDetailPanel()
          )}
        </>
      )}

      <EmailComposeModal
        open={composeOpen}
        mode="compose"
        onClose={() => { setComposeOpen(false); if (activeFolder === 'sent') loadSent(); }}
      />
      <EmailComposeModal
        open={replyOpen}
        mode="reply"
        onClose={() => { setReplyOpen(false); setReplyData(null); }}
        replyData={replyData ? { threadId: replyData.threadId || '', to: replyData.to || '', subject: replyData.subject || '', originalBody: replyData.originalBody } : undefined}
      />

      <Dialog open={templateFormOpen} onOpenChange={setTemplateFormOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Modifica Template' : 'Nuovo Template'}</DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Modifica i campi del template email.' : 'Crea un nuovo template email.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome Template</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Benvenuto Cliente" />
              </div>
              <div>
                <Label>Oggetto</Label>
                <div className="flex gap-1">
                  <Input value={templateForm.subject} onChange={e => setTemplateForm(f => ({ ...f, subject: e.target.value }))} placeholder="Oggetto email" className="flex-1" />
                  <Button variant="outline" size="sm" onClick={() => { setVarPickerTarget('subject'); setVarPickerOpen(true); }}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Corpo Email</Label>
                <Button variant="outline" size="sm" onClick={() => { setVarPickerTarget('body_html'); setVarPickerOpen(true); }}>
                  <Plus className="w-3 h-3 mr-1" /> Variabile
                </Button>
              </div>
              <RichTextEditor
                ref={bodyEditorRef}
                value={templateForm.body_html}
                onChange={v => setTemplateForm(f => ({ ...f, body_html: v }))}
                placeholder="Scrivi il corpo dell'email..."
              />
            </div>
            <div>
              <Label>Firma</Label>
              <RichTextEditor
                value={templateForm.signature_html}
                onChange={v => setTemplateForm(f => ({ ...f, signature_html: v }))}
                placeholder="Firma opzionale..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={templateForm.is_default}
                onCheckedChange={c => setTemplateForm(f => ({ ...f, is_default: c as boolean }))}
              />
              <Label className="cursor-pointer">Template predefinito</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTemplateFormOpen(false)}>Annulla</Button>
              <Button onClick={saveTemplate} disabled={savingTemplate}>
                {savingTemplate && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTemplate ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={varPickerOpen} onOpenChange={setVarPickerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Inserisci Variabile</DialogTitle>
            <DialogDescription>Seleziona una variabile da inserire nel template.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[300px]">
            {variableCategories.map(cat => (
              <div key={cat.category.id}>
                <button
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded"
                  onClick={() => toggleVarCat(cat.category.id)}
                >
                  {expandedVarCats.has(cat.category.id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  {cat.category.display_name}
                </button>
                {expandedVarCats.has(cat.category.id) && (
                  <div className="ml-5 space-y-0.5">
                    {cat.variables.map(v => (
                      <button
                        key={v.id}
                        className="block w-full text-left px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 rounded"
                        onClick={() => { insertVariable(v.variable_key); setVarPickerOpen(false); }}
                      >
                        {v.display_name}
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">({v.example_value})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Elimina Template</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare questo template? L'azione non puo' essere annullata.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Annulla</Button>
            <Button variant="destructive" onClick={() => deleteConfirmId && deleteTemplate(deleteConfirmId)}>Elimina</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
